/**
 * New Year cross-stitch patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearCrossStitchPatternsPack: ToilePack = {
    slug: 'new-year-cross-stitch-patterns',
    category: 'season',
    name: 'New Year cross-stitch patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'firework', label: 'Firework chart', subject: 'A firework burst with radiating sparks'},
        {key: 'glass', label: 'Glass chart', subject: 'A champagne flute with rising bubbles'},
        {key: 'cheers', label: 'Cheers chart', subject: 'The word "CHEERS" spelled exactly in bold block letters'},
    ],
    styles: [
        {key: 'simple', label: 'Simple lines', style: 'bold single-weight black outline drawing, large open shapes, clean closed line art', palette: 'Black ink only', framing: 'Centered on a pure white page with a white margin, the full page used'},
        {key: 'detailed', label: 'Detailed', style: 'intricate fine black line art, dense decorative detail, clean closed line art', palette: 'Black ink only', framing: 'Filling the page edge to edge inside a thin white margin'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn marker doodle line art, loose confident strokes, clean closed line art', palette: 'Black ink only', framing: 'Scattered across the page with a white margin'},
    ],
    directions: ['a star instead', 'as a smaller chart', 'with a border pattern'],
    directionHint: 'try: a star instead',
    seo: {
        title: 'New Year cross stitch: firework, glass, cheers',
        metaDescription: 'Nine New Year cross-stitch charts — a firework, a champagne flute, a CHEERS lettering chart — in simple, detailed or doodle grids.',
        h1: 'New Year cross stitch patterns: firework, glass and CHEERS',
        tagline: 'Nine charts, three celebration motifs, three grid styles each — ruled square by square with a key.',
        intro: "A cross-stitch chart for midnight: a firework burst, a champagne flute with rising bubbles, the word CHEERS in bold block letters, each charted three ways — bold simple symbols, a detailed dense grid, or a looser doodle-style chart. Every chart carries a symbol key in the corner. Print it and tape it to the frame, or ask for a different motif and it's charted again.",
        useCases: [
            'a chart taped to the embroidery frame',
            "a small New Year's gift for a fellow stitcher",
            'a first project for a new stitcher',
            'a celebration chart swapped by direction',
        ],
        faq: [
            {q: 'What count is the chart sized for?', a: 'The grid is drawn with a heavier rule every ten squares so counting stays easy, whichever of the three styles you pick.'},
            {q: 'Is the CHEERS lettering exact?', a: 'Yes — the word charts exactly as CHEERS in bold block letters, no substitutions.'},
            {q: 'Can I get a different celebration motif?', a: "Yes — use the direction field to ask for a different motif, like a star or a border pattern, and it's charted again in all three styles."},
        ],
    },
}
